import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EMPTY, Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import Url from "url-parse";

import {
  GraphQLConnector,
  GraphQLFilter,
  GraphQLGroupBy,
  GraphQLOrderBy,
  GraphQlQuery,
  GraphQlResultsSeriesOptions,
  IGraphQlQueryExecutor,
  IGraphQlService,
} from "./IGraphQL.service";
import { AntiForgeryToken } from "../../../main";
import { GraphQLComparision } from "../../enums/GraphQLComparision.enum";

  const enumFields = [
  'eveCdrAnsweredType',
  'eveCdrCallType',
  'telepoCdrAnsweredType',
  // add others as needed
];

@Injectable()
export class GraphQlService implements IGraphQlQueryExecutor, IGraphQlService {
  constructor(
    @Inject("BASE_REPORTING") public reportingUrl: string,
    private http: HttpClient
  ) {}

  //path:startTime comparison:GREATER_THAN_OR_EQUAL value:"2021-12-15 00:00:00"

  getGraphQlResults(query: GraphQlQuery): Observable<any> {
    const datasetField = this.transformDatasetName(query.dataset);

    console.log(`GraphQL query: ${datasetField}`, query);

    const take = query.pageSize === undefined || query.pageSize == null
                ? 10
                : query.pageSize < 0
                ? 2147483646
                : query.pageSize

    const skip = (query.page ?? 0) * take;
    const whereInput = this.buildWhereInput(query.wheres);
    const groupByInput = this.buildGroupByInput(query.groupBys);
    const orderByInput = this.buildOrderInput(query.orderBy);
    const graphQlQuery = `query {
          ${datasetField}(
            ${whereInput}
            ${groupByInput}
            ${orderByInput}
            skip: ${skip}
            take: ${take}
          )
          {
            totalCount 
            items {
              ${query.selects
                .map((select) => {
                  if (select.arguments == null) return select.field;
                  return (
                    select.field +
                    "(" +
                    Object.entries(select.arguments)
                      .map(([key, value]) => key + ":" + value)
                      .join(", ") +
                    ")"
                  );
                })
                .join(", ")}
            }
          }
      }`;

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const payload = {
      query: graphQlQuery,
      // variables: {}     // only if you have real variables to pass
    };

    console.log(`${query.dataset}: \n`, graphQlQuery);
    return this.http
      .post(this.reportingUrl + "/graphql", payload, {
        headers,
        observe: "response",
      })
      .pipe(
        map((resp: any) => {
          const body = resp.body;
          const datasetOld = query.dataset; // e.g. 'companiesPage'
          const datasetNew = this.transformDatasetName(datasetOld); // e.g. 'companies'
          console.log(
            `Transformed dataset name: ${datasetOld} -> ${datasetNew}`
          );
          if (body?.data && body.data[datasetNew] !== undefined) {
            // Reverse map: make data.companiesPage point to data.companies
            body.data[datasetOld] = body.data[datasetNew];
            // console.log(`body.data[${datasetOld}]`, body.data[datasetOld]);
            // Optionally remove the new field
            delete body.data[datasetNew];
          }
          return body;
        }),
        catchError((err) => {
          if (err.name == "HttpErrorResponse") {
            var url = new Url(err.url);

            //has session expired
            if (url.pathname == "/Login/Login") {
              window.location.reload();
              return EMPTY;
            }
          }
          return err;
        })
      );
  }

  executeGraphQl(graphQl: string): Observable<any> {
    const headers = {
      "Content-Type": "application/graphql",
    };

    return this.http
      .post(this.reportingUrl + "/graphql", graphQl, {
        headers,
        observe: "response",
      })
      .pipe(
        map((resp: any) => resp.body),
        catchError((err) => {
          if (err.name == "HttpErrorResponse") {
            var url = new Url(err.url);

            //has session expired
            if (url.pathname == "/Login/Login") {
              window.location.reload();
              return EMPTY;
            }
          }
          return err;
        })
      );
  }

  fromWheres(wheres: GraphQLFilter[]): String {
    return wheres.map((where) => this.fromWhere(where)).join(", ");
  }

  fromWhere(where: GraphQLFilter): String {
    return `{ path:${where.path} comparison:${
      GraphQLComparision[where.comparison]
    } value:${JSON.stringify(where.value)} ${
      where.connector == null
        ? ""
        : `connector:${GraphQLConnector[where.connector]}`
    } ${
      where.groupedExpressions?.length > 0
        ? `groupedExpressions: [${this.fromWheres(where.groupedExpressions)}]`
        : ""
    }}`;
  }

  getSchema(): Observable<any> {
    const headers = {
      "Content-Type": "application/graphql",
    };

    return this.http.post(
      this.reportingUrl + "/graphql",
      `{
  __schema {
    queryType {
      name
    }
    mutationType {
      name
    }
    subscriptionType {
      name
    }
    types {
      ...FullType
    }
    directives {
      name
      description
      locations
      args {
        ...InputValue
      }
    }
  }
}
fragment FullType on __Type {
  kind
  name
  description
  fields(includeDeprecated: true) {
    name
    description
    args {
      ...InputValue
    }
    type {
      ...TypeRef
    }
    isDeprecated
    deprecationReason
  }
  inputFields {
    ...InputValue
  }
  interfaces {
    ...TypeRef
  }
  enumValues(includeDeprecated: true) {
    name
    description
    isDeprecated
    deprecationReason
  }
  possibleTypes {
    ...TypeRef
  }
}
fragment InputValue on __InputValue {
  name
  description
  type {
    ...TypeRef
  }
  defaultValue
}
fragment TypeRef on __Type {
  kind
  name
  ofType {
    kind
    name
    ofType {
      kind
      name
      ofType {
        kind
        name
        ofType {
          kind
          name
          ofType {
            kind
            name
            ofType {
              kind
              name
              ofType {
                kind
                name
              }
            }
          }
        }
      }
    }
  }
}`,
      {
        headers: { "Content-Type": "application/graphql" },
      }
    );
  }

  getGraphQlResultsSeries(
    query: GraphQlQuery,
    options: GraphQlResultsSeriesOptions
  ): Observable<any> {
    var resultObservable: Observable<any> = this.getGraphQlResults(query).pipe(
      map((result) => {
        const data = Object.entries(
          result.data[query.dataset].items
            .map((i) => {
              const nameSelect = query.groupBys[0].path;
              const name = i[nameSelect];

              return {
                seriesKey: query.groupBys
                  .slice(1)
                  .map((g) => i[g.path])
                  .join(", "),
                wheres: query.groupBys
                  .map((g) => {
                    if (g.timeUnit != null) {
                      var from = new Date(i[g.path]);
                      var to = new Date(i[g.path]);

                      if (g.timeUnit == "HOURS")
                        to.setHours(from.getHours() + 1);
                      else if (g.timeUnit == "DAYS")
                        to.setDate(from.getDate() + 1);
                      else throw "Failed";

                      return [
                        {
                          path: g.path,
                          comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
                          value: "" + i[g.path],
                        },
                        {
                          path: g.path,
                          comparison: GraphQLComparision.LESS_THAN,
                          value: "" + to.toJSON(),
                        },
                      ];
                    }

                    return [
                      {
                        path: g.path,
                        comparison: GraphQLComparision.EQUAL,
                        value: i[g.path],
                      },
                    ];
                  })
                  .flat(),
                name: "" + name,
                value:
                  options?.valueConverter != null
                    ? options.valueConverter(i[query.selects[0].field])
                    : i[query.selects[0].field],
                data: i,
              };
            })
            .groupBy((r) => r.seriesKey)
        )
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map((e) => {
            const name =
              options?.nameTemplate == null
                ? e[0]
                : new Function(`return \`${options.nameTemplate}\`;`).call(
                    e[1][0].data
                  );
            return {
              name: name,
              series: (<[{ name; value }]>e[1])
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((r) =>
                  Object.assign({}, r, {
                    name: isDate(r.name) ? new Date(r.name) : r.name,
                  })
                )
                .sort((a, b) => (a.name < b.name ? -1 : 1)),
            };
          });

        return data;
      })
    );

    if (options?.operator != null) {
      resultObservable = resultObservable.pipe(options.operator);
    }

    return resultObservable;
  }

  /**
   * Transform dataset names for HotChocolate:
   * 1) Strip trailing "Page"
   * 2) Pluralize: add "s", or for words ending in consonant+y, replace "y" with "ies"
   * 3) Lowercase first letter (camelCase)
   */
  private transformDatasetName(raw: string): string {
    let base = raw.replace(/Page$/, "");
    if (!base.endsWith("s")) {
      const len = base.length;
      const lastChar = base.charAt(len - 1);
      const secondLast = len > 1 ? base.charAt(len - 2).toLowerCase() : "";
      // If ends with consonant + 'y', use 'ies'
      if (lastChar === "y" && !["a", "e", "i", "o", "u"].includes(secondLast)) {
        base = base.slice(0, -1) + "ies";
      } else {
        base = base + "s";
      }
    }
    // Lowercase first letter for camelCase
    return base.charAt(0).toLowerCase() + base.slice(1);
  }

  private mapComparison(op: GraphQLComparision | string): string {
    switch (op) {
      case GraphQLComparision.EQUAL:
        return "eq";
      case GraphQLComparision.NOT_EQUAL:
        return "neq";
      case GraphQLComparision.GREATER_THAN:
        return "gt";
      case GraphQLComparision.GREATER_THAN_OR_EQUAL:
        return "gte";
      case GraphQLComparision.LESS_THAN:
        return "lt";
      case GraphQLComparision.LESS_THAN_OR_EQUAL:
        return "lte";
      case GraphQLComparision.CONTAINS:
        return "contains";
      case GraphQLComparision.DOES_NOT_CONTAIN:
        return "notContains";
      case GraphQLComparision.STARTS_WITH:
        return "startsWith";
      case GraphQLComparision.ENDS_WITH:
        return "endsWith";
      case GraphQLComparision.IN:
        return "in";
      case GraphQLComparision.LIKE:
        return "like";
      case GraphQLComparision.NOT_IN:
        return "notIn";
      case GraphQLComparision.LAST:
        return "last";
      case GraphQLComparision.IS_DESCENDANT_OF:
        return "isDescendantOf";
      default:
        return String(op).toLowerCase();
    }
  }

  private formatValue( path: string, val: any,): string {
    const formattedValue = `${JSON.stringify(val)}`;

    if (path && enumFields.includes(path)) {
      return val; // Unquoted for GraphQL enums
    }

    return typeof val === "string" ? `"${val}"` : `${formattedValue}`;
  }

  // helper: comparisons that require array values
private isArrayOp(op: GraphQLComparision): boolean {
  return op === GraphQLComparision.IN || op === GraphQLComparision.NOT_IN;
}

private normalizeValue(op: GraphQLComparision, path: string, value: any): string {
  if (this.isArrayOp(op)) {
    const arr = Array.isArray(value) ? value : [value];
    return JSON.stringify(arr); // e.g. [6809]
  }
  return this.formatValue(path, value);
}

// render a single leaf filter OR a nested group (returns a GraphQL object string or null)
private renderFilterNode(node: GraphQLFilter): string | null {
  // If this node is a group, render its children recursively
  if (node?.groupedExpressions?.length) {
    const inner = this.renderFilterGroup(node.groupedExpressions);
    return inner ? `{ ${inner} }` : null;
  }

  // Leaf: needs a path, comparison, and value
  if (!node?.path) return null;

  const field = node.path.charAt(0).toLocaleLowerCase() + node.path.slice(1);
  const op    = this.mapComparison(node.comparison);
  const val   = this.normalizeValue(node.comparison, node.path, node.value);

  return `{ ${field}: { ${op}: ${val} } }`;
}

// render an array of siblings with connectors (default AND)
private renderFilterGroup(nodes: GraphQLFilter[]): string {
  const orItems: string[] = [];
  const andItems: string[] = [];

  for (const n of nodes) {
    const rendered = this.renderFilterNode(n);
    if (!rendered) continue;

    if (n.connector === GraphQLConnector.OR) {
      orItems.push(rendered);
    } else {
      andItems.push(rendered); // default AND
    }
  }

  const parts: string[] = [];
  if (orItems.length)  parts.push(`or: [${orItems.join(', ')}]`);
  if (andItems.length) parts.push(`and: [${andItems.join(', ')}]`);

  return parts.join(', ');
}

// PUBLIC: build `where: { ... }`
private buildWhereInput(wheres?: GraphQLFilter[]): string {
  if (!wheres?.length) return '';
  const groupBody = this.renderFilterGroup(wheres);
  return groupBody ? `where: { ${groupBody} }` : '';
}


  /**
   * Build the `groupBy` input according to HotChocolate conventions
   */
  private buildGroupByInput(groupBys?: GraphQLGroupBy[]): string {
    if (!groupBys || groupBys.length === 0) {
      return "";
    }

    const bits = groupBys
      .map((g) => {
        // Uppercase the first letter to match C# property names
        const field = g.path.charAt(0).toUpperCase() + g.path.slice(1);
        if (g.timeUnit) {
          return `{ path: ${field} timeUnit: ${g.timeUnit} }`;
        }
        return `{ path: ${field} }`;
      })
      .join(", ");

    return `groupBy: [${bits}]`;
  }

  /**
   * Build the `order` input according to HotChocolate conventions
   */
  private buildOrderInput(orders?: GraphQLOrderBy[]): string {
    if (!orders || orders.length === 0) {
      return "";
    }

    const bits = orders
      .map((o) => {
        const field = o.path.charAt(0).toLocaleLowerCase() + o.path.slice(1);
        // Only true yields DESC, undefined or false yields ASC
        const direction = o.descending ? "DESC" : "ASC";
        return `{ ${field}: ${direction} }`;
      })
      .join(" ");

    return `order: [${bits}]`;
  }
}
const dateRegex = new RegExp(
  "^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d{1,3}|)Z$"
);

var isDate = function (str) {
  return dateRegex.test(str);
};

export const StringComparisons = [
  GraphQLComparision.CONTAINS,
  GraphQLComparision.DOES_NOT_CONTAIN,
  GraphQLComparision.STARTS_WITH,
  GraphQLComparision.ENDS_WITH,
  GraphQLComparision.LIKE,
  GraphQLComparision.IN,
  GraphQLComparision.NOT_IN,
];

export const NumberComparisons = [
  GraphQLComparision.LESS_THAN,
  GraphQLComparision.LESS_THAN_OR_EQUAL,
  GraphQLComparision.GREATER_THAN_OR_EQUAL,
  GraphQLComparision.GREATER_THAN,
  GraphQLComparision.EQUAL,
  GraphQLComparision.NOT_EQUAL,
  GraphQLComparision.IN,
  GraphQLComparision.NOT_IN,
];

export const DatasetDefinitions = {
  telepoCdrsPage: {
    selects: {
      duration: {},
      type: {
        toName: (value) => {
          const mapping = {
            1: "OUTGOING",
            2: "INCOMING",
          };

          return mapping[value] ?? value;
        },
      },
    },
    paths: {
      callId: {
        type: "string",
        comparisons: StringComparisons,
      },
      result: {
        type: "number",
        comparisons: NumberComparisons,
      },
      startTime: {
        type: "date",
        comparisons: NumberComparisons,
      },
      sourceOwner: {
        type: "eveUserName",
        comparisons: StringComparisons,
      },
      sourcePhoneNumberAreaCode: {
        type: "string",
        comparisons: StringComparisons,
      },
      targetPhoneNumberAreaCode: {
        type: "string",
        comparisons: StringComparisons,
      },
    },
    sorts: [],
    groupBys: {
      startTimeMinute: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setMinutes(endTime.getMinutes() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeHour: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setHours(endTime.getHours() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeDay: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setDate(endTime.getDay() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeMonth: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setMonth(endTime.getMonth() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      sourceOwner: {
        select: "sourceOwner",
      },
      result: {
        select: "result",
      },
      type: {
        select: "type",
      },
      orgId: {
        select: "orgId",
      },
      sourcePhoneNumberAreaCode: {
        select: "sourcePhoneNumberAreaCode",
      },
      targetPhoneNumberAreaCode: {
        select: "targetPhoneNumberAreaCode" /*,
        getWheres: v => {

          var areaCode = new String(v);
          return [
            { path: "targetPhoneNumberAreaCode", comparison: GraphQLComparision.EQUAL, value: "" + areaCode }
          ]
        }*/,
      },
    },
  },
  eveCdrsPage: {
    selects: {
      duration: {},
      type: {
        toName: (value) => {
          const mapping = {
            1: "OUTGOING",
            2: "INCOMING",
          };

          return mapping[value] ?? value;
        },
      },
      eveCdrCallTypeId: {
        toName: (value) => {
          const mapping = {
            0: "Outgoing",
            1: "Incoming",
            2: "Internal",
          };

          return mapping[value] ?? value;
        },
      },
    },
    paths: {
      callId: {
        type: "string",
        comparisons: StringComparisons,
      },
      result: {
        type: "number",
        comparisons: NumberComparisons,
      },
      startTime: {
        type: "date",
        comparisons: NumberComparisons,
      },
      sourceOwner: {
        type: "eveUserName",
        comparisons: StringComparisons,
      },
      sourcePhoneNumberAreaCode: {
        type: "string",
        comparisons: StringComparisons,
      },
      targetPhoneNumberAreaCode: {
        type: "string",
        comparisons: StringComparisons,
      },
      callDestination: {
        type: "string",
        comparisons: StringComparisons,
      },
      eveCdrCallTypeId: {
        type: "string",
        comparisons: NumberComparisons,
      },
    },
    sorts: [],
    groupBys: {
      startTimeMinute: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setMinutes(endTime.getMinutes() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeHour: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setHours(endTime.getHours() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeDay: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setDate(endTime.getDay() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeMonth: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setMonth(endTime.getMonth() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      sourceOwner: {
        select: "sourceOwner",
      },
      result: {
        select: "result",
      },
      type: {
        select: "type",
      },
      orgId: {
        select: "orgId",
      },
      callDestination: {
        select: "callDestination",
      },
      eveCdrCallTypeId: {
        select: "eveCdrCallTypeId",
      },
      sourcePhoneNumberAreaCode: {
        select: "sourcePhoneNumberAreaCode",
      },
      targetPhoneNumberAreaCode: {
        select: "targetPhoneNumberAreaCode" /*,
        getWheres: v => {

          var areaCode = new String(v);
          return [
            { path: "targetPhoneNumberAreaCode", comparison: GraphQLComparision.EQUAL, value: "" + areaCode }
          ]
        }*/,
      },
    },
  },
  eveCdrOwnersPage: {
    selects: {
      duration: {},
      type: {
        toName: (value) => {
          const mapping = {
            1: "OUTGOING",
            2: "INCOMING",
          };

          return mapping[value] ?? value;
        },
      },
      eveCdrCallTypeId: {
        toName: (value) => {
          const mapping = {
            0: "Outgoing",
            1: "Incoming",
            2: "Internal",
          };

          return mapping[value] ?? value;
        },
      },
    },
    paths: {
      callId: {
        type: "string",
        comparisons: StringComparisons,
      },
      result: {
        type: "number",
        comparisons: NumberComparisons,
      },
      startTime: {
        type: "date",
        comparisons: NumberComparisons,
      },
      sourceOwner: {
        type: "eveUserName",
        comparisons: StringComparisons,
      },
      sourcePhoneNumberAreaCode: {
        type: "string",
        comparisons: StringComparisons,
      },
      targetPhoneNumberAreaCode: {
        type: "string",
        comparisons: StringComparisons,
      },
      callDestination: {
        type: "string",
        comparisons: StringComparisons,
      },
      eveCdrCallTypeId: {
        type: "string",
        comparisons: NumberComparisons,
      },
    },
    sorts: [],
    groupBys: {
      startTimeMinute: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setMinutes(endTime.getMinutes() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeHour: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setHours(endTime.getHours() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeDay: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setDate(endTime.getDay() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      startTimeMonth: {
        select: "startTime",
        getWheres: (v) => {
          var endTime = new Date(v);
          endTime.setMonth(endTime.getMonth() + 1);
          return [
            {
              path: "startTime",
              comparison: GraphQLComparision.GREATER_THAN_OR_EQUAL,
              value: "" + v,
            },
            {
              path: "startTime",
              comparison: GraphQLComparision.LESS_THAN,
              value: "" + endTime.toJSON(),
            },
          ];
        },
      },
      sourceOwner: {
        select: "sourceOwner",
      },
      result: {
        select: "result",
      },
      type: {
        select: "type",
      },
      orgId: {
        select: "orgId",
      },
      callDestination: {
        select: "callDestination",
      },
      eveCdrCallTypeId: {
        select: "eveCdrCallTypeId",
      },
      sourcePhoneNumberAreaCode: {
        select: "sourcePhoneNumberAreaCode",
      },
      targetPhoneNumberAreaCode: {
        select: "targetPhoneNumberAreaCode" /*,
        getWheres: v => {

          var areaCode = new String(v);
          return [
            { path: "targetPhoneNumberAreaCode", comparison: GraphQLComparision.EQUAL, value: "" + areaCode }
          ]
        }*/,
      },
    },
  },
  telepoGroupStatsPage: {
    selects: ["averageQueueTime"],
    paths: {
      Id2: {
        type: "string",
        comparisons: StringComparisons,
      },
      number: {
        type: "number",
        comparisons: NumberComparisons,
      },
      startTime: {
        type: "date",
        comparisons: NumberComparisons,
      },
      sourceOwner: {
        type: "eveUserName",
        comparisons: StringComparisons,
      },
    },
    sorts: [],
    groupBys: {
      startTimeMinute: {
        select: "startTime",
      },
      startTimeHour: {
        select: "startTime",
      },
      startTimeDay: {
        select: "startTime",
      },
      startTimeMonth: {
        select: "startTime",
      },
      functionNumber: {
        select: "functionNumber",
      },
    },
  },
  telepoGroupEveUserStatsPage: {
    selects: [
      "handledCalls",
      "averageCallDuration",
      "missedCalls",
      "declinedCalls",
    ],
    paths: {
      Id3: {
        type: "string",
        comparisons: StringComparisons,
      },
      number: {
        type: "number",
        comparisons: NumberComparisons,
      },
      startTime: {
        type: "date",
        comparisons: NumberComparisons,
      },
      sourceOwner: {
        type: "eveUserName",
        comparisons: StringComparisons,
      },
    },
    sorts: [],
    groupBys: {
      startTimeMinute: {
        select: "startTime",
      },
      startTimeHour: {
        select: "startTime",
      },
      startTimeDay: {
        select: "startTime",
      },
      startTimeMonth: {
        select: "startTime",
      },
      functionNumber: {
        select: "functionNumber",
      },
      eveUserName: {
        select: "eveUserName",
      },
    },
  },
};
